import { cn } from "@/common/helpers/utils";
import type { ComponentType, CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import type { VehicleLoaderProps } from "./vehicle-loader/loader-svg-parts";
import { VehicleLoaderHeadlights } from "./vehicle-loader/variants/vehicle-loader-headlights";
import { VehicleLoaderLane } from "./vehicle-loader/variants/vehicle-loader-lane";
import { VehicleLoaderLift } from "./vehicle-loader/variants/vehicle-loader-lift";
import { VehicleLoaderRoad } from "./vehicle-loader/variants/vehicle-loader-road";
import { VehicleLoaderSparkle } from "./vehicle-loader/variants/vehicle-loader-sparkle";
import { VehicleLoaderWheel } from "./vehicle-loader/variants/vehicle-loader-wheel";
import "./vehicle-loader/vehicle-loader.css";

export enum LoaderSize {
  Small = 14,
  Medium = 50,
  Large = 100,
}

export enum LoaderVariant {
  Road,
  Lift,
  Headlights,
  Lane,
  Sparkle,
  Wheel,
}

const LOADER_VARIANT_COMPONENTS: Record<LoaderVariant, ComponentType<VehicleLoaderProps>> = {
  [LoaderVariant.Road]: VehicleLoaderRoad,
  [LoaderVariant.Lift]: VehicleLoaderLift,
  [LoaderVariant.Headlights]: VehicleLoaderHeadlights,
  [LoaderVariant.Lane]: VehicleLoaderLane,
  [LoaderVariant.Sparkle]: VehicleLoaderSparkle,
  [LoaderVariant.Wheel]: VehicleLoaderWheel,
};

export interface LoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: LoaderSize;
  variant?: LoaderVariant;
  fullscreen?: boolean;
  isChild?: boolean;
  animationClassName?: string;
  /** Uses the surrounding text color; only affects {@link LoaderVariant.Wheel}. */
  inheritColor?: boolean;
}

export function Loader({
  size = LoaderSize.Small,
  variant = LoaderVariant.Road,
  fullscreen = false,
  isChild = false,
  className,
  animationClassName,
  inheritColor,
  style,
  ...props
}: LoaderProps) {
  const { t } = useTranslation("common");
  const LoaderAnimation = LOADER_VARIANT_COMPONENTS[variant];
  const fullscreenHeightClass = isChild ? "h-full" : "h-screen";

  return (
    <div
      role="status"
      aria-label={t("Loading")}
      data-loader-variant={variant}
      className={cn("flex items-center justify-center", fullscreen && fullscreenHeightClass, className)}
      style={{ "--loader-size": `${size}px`, ...style } as CSSProperties}
      {...props}
    >
      <LoaderAnimation
        className={cn("h-(--loader-size) w-auto max-w-full shrink-0", animationClassName)}
        inheritColor={inheritColor}
      />
    </div>
  );
}
